/**
 * GroceryListEngine — THE list surface, portal-wide and host-agnostic.
 *
 * It takes its household from PROPS (a session token), never from `useAuth()`
 * or the URL, so the property hub tile and the showroom can embed it unchanged
 * later. Grocery wording only: customer, household, member, owner, store, list.
 *
 * Authority vs attribution: the "Who's adding?" chips are attribution — one
 * tap, no PIN — so a parent can see who added what. A PIN is only ever used for
 * approve and pay (later phases).
 *
 * The play layer (points, streak, celebration, sound) comes from
 * components/engagement/* and is shared with every other vertical. All of it is
 * decorative: no animation sits over a control and nothing waits on one.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
    Plus, Trash2, Undo2, Share2, Check, Users, Store, Loader2, ShoppingBasket,
    HelpCircle, Heart, Home, Receipt, AlertTriangle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import GroceryIcon from './GroceryIcon';
import GroceryAddFlow, { FavouriteRoller } from './GroceryAddFlow';
import PointsChip from '../engagement/PointsChip';
import RewardProgress from '../engagement/RewardProgress';
import StreakChip from '../engagement/StreakChip';
import SoundToggle from '../engagement/SoundToggle';
import MemberAvatar from '../engagement/MemberAvatar';
import CelebrationBurst from '../engagement/CelebrationBurst';
import EmptyState from '../engagement/EmptyState';
import { tickFeedback } from '../engagement/playPrefs';
import AskChef, { ChefPersonaCard, ChefWelcomeNote, useAskChef } from './AskChef';
import GroceryTour, { useGroceryTour } from './GroceryTour';
import GrocerySendList from './GrocerySendList';
import GroceryOrderStatus from './GroceryOrderStatus';
import GroceryModeToggle from './GroceryModeToggle';
import TreasureCard, { ADDED_MS } from './TreasureCard';
import { QtyEditRow, QtyPanel } from './GroceryQtyPanel';
import { ProductOptionsCard } from './ProductOptionsCard';
import { BrandPattern } from './BrandPattern';
import { BillingPanel } from './BillingPanel';
import { recoverDemoSession, consumeDemoRefreshed } from './GroceryDemo';
import GrocerySharePanel from './GrocerySharePanel';

const PO_EMPTY = { form: '', type: '', brand: '', note: '' };

const API = (typeof window !== 'undefined' ? window.location.origin : '');
const POLL_MS = 5000;
const FRIDGE_ICON = '/static/icons/grocery/1f9ca.png';     // ice cube — "my fridge"
const PANTRY_ICON = '/static/icons/grocery/1f96b.png';     // can — "my pantry"
const EMPTY_ICON = '/static/icons/grocery/1f6d2.png';      // shopping trolley

const api = async (path, { method = 'GET', body, session, lang } = {}) => {
    const sep = path.includes('?') ? '&' : '?';
    const r = await fetch(`${API}/api/grocery${path}${sep}lang=${lang || 'en'}`, {
        method,
        headers: { 'Content-Type': 'application/json', 'X-Grocery-Session': session },
        body: body ? JSON.stringify(body) : undefined,
    });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `http_${r.status}`);
    return r.status === 204 ? {} : r.json();
};

export default function GroceryListEngine({ session, onMembers, compact = false }) {
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || 'en').slice(0, 2);
    const [data, setData] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [offline, setOffline] = useState(false);
    const fails = useRef(0);
    const [whoId, setWhoId] = useState(null);
    const [lastRemoved, setLastRemoved] = useState(null);
    const [busy, setBusy] = useState(false);
    const [checkMode, setCheckMode] = useState(false);
    const [ticking, setTicking] = useState(null);          // item id mid-animation
    const [tappedIcon, setTappedIcon] = useState(null);
    const [celebrate, setCelebrate] = useState(false);
    const [favShelf, setFavShelf] = useState('fridge');
    const [addOpen, setAddOpen] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const [favSheet, setFavSheet] = useState(null);
    const [qtyFor, setQtyFor] = useState(null);     // the quantity panel's food
    const [po, setPo] = useState(null);             // its kind/brand chips
    const [poError, setPoError] = useState(false);  // the chips could not be read
    const [poVal, setPoVal] = useState(PO_EMPTY);
    const [qtyEdit, setQtyEdit] = useState(null);   // changing a number on the list
    const [flashId, setFlashId] = useState(null);   // the row that just arrived
    const flashTimer = useRef(null);
    const [chefOpenWith, setChefOpenWith] = useState(null);
    const [justAdded, setJustAdded] = useState(null);
    const [tourGoTo, setTourGoTo] = useState(null);
    // While the order detail is open the chef steps aside: it used to sit on the
    // Text preview the family is meant to read.
    const [readingOrder, setReadingOrder] = useState(false);
    const celebrated = useRef(false);
    const addedTimer = useRef(null);
    // Ask your chef: a shared, vertical-agnostic assistant block.
    const chef = useAskChef(session, lang);
    // The guided tour: per member, server-side, never auto-opens on a demo.
    const tour = useGroceryTour(session);

    const load = useCallback(async () => {
        try {
            const d = await api('/list', { session, lang });
            setData(d);
            setLoadError(false);
            fails.current = 0;
            setOffline(false);
        } catch (e) {
            // A DEMO THAT WAS RESET IN ANOTHER TAB TAKES A NEW SESSION AND
            // RELOADS. Anything else keeps the last good list and says so.
            if (String(e.message).includes('401') && await recoverDemoSession()) return;
            fails.current += 1;
            setLoadError(true);
            if (fails.current >= 2) setOffline(true);
        }
    }, [session, lang]);

    useEffect(() => {
        if (consumeDemoRefreshed()) toast.success(t('grocery.demoRefreshed'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        // Live refresh within seconds — but NOT while someone is choosing in the
        // add flow: a refresh under a thumb moves the thing being tapped.
        if (addOpen || favSheet) return undefined;
        const id = setInterval(load, POLL_MS);
        return () => clearInterval(id);
    }, [load, addOpen, favSheet]);

    const members = data?.members || [];
    // The chip starts on whoever opened the card, so a kid's additions are
    // attributed without them having to think about it — BUT ONLY ONCE. This
    // used to run on every render where `whoId` was null, which meant tapping
    // Fridge (whoId = null) was instantly undone and the chip never stuck.
    const whoDefaulted = useRef(false);
    useEffect(() => {
        if (whoDefaulted.current || !data?.session_member_id) return;
        whoDefaulted.current = true;
        setWhoId(data.session_member_id);
    }, [data]);

    const who = useMemo(() => members.find((m) => m.id === whoId) || null, [members, whoId]);
    const owner = useMemo(() => members.find((m) => m.role === 'owner') || null, [members]);
    const sessionMember = useMemo(
        () => members.find((m) => m.id === data?.session_member_id) || null, [members, data]);
    // A kid-friendly surface: add-only, no approve, no pay. Bigger targets, their
    // own corner, and never a timer or a countdown.
    const kidView = !!sessionMember && sessionMember.can_add
        && !sessionMember.can_approve && !sessionMember.can_pay;

    const collator = useMemo(
        () => new Intl.Collator(lang, { sensitivity: 'base', numeric: true }), [lang]);

    const byCategory = useMemo(() => {
        const groups = {};
        (data?.items || []).forEach((i) => {
            const key = i.category || 'other';
            (groups[key] = groups[key] || []).push(i);
        });
        return Object.keys(groups)
            .sort((a, b) => collator.compare(
                groups[a][0]?.category_name || t(`grocery.cat.${a}`, a),
                groups[b][0]?.category_name || t(`grocery.cat.${b}`, b)))
            .map((k) => [k, groups[k].sort((a, b) => collator.compare(a.name || '', b.name || ''))]);
    }, [data, t, collator]);

    // The tour's heart step anchors the first row that actually HAS a heart: a
    // free-text food has no catalog entry and so no favourite button.
    const heartRowId = useMemo(() => ((data?.items || [])
        .find((i) => i.catalog_id && i.status !== 'done') || {}).id || null, [data]);

    // Which foods are already favourites — the heart on a row reads from this.
    const favIds = useMemo(() => new Set(
        [...(data?.favourites?.fridge || []), ...(data?.favourites?.pantry || [])]
            // A typed food has no catalogue id, so its heart is keyed on the
            // household's own saved item instead.
            .map((f) => f.catalog_id || f.saved_id).filter(Boolean)), [data]);

    const add = async ({ name, catalog_id, unit, category, options }, qty = 1, opts = {}) => {
        if (who && who.can_add === false) { toast.error(t('grocery.cannotAdd')); return; }
        setBusy(true);
        setTappedIcon(catalog_id || name);
        try {
            const r = await api('/items', { method: 'POST', session, lang,
                body: { name, catalog_id, unit, category, qty, member_id: whoId,
                    // Everyone chip: the household, nobody in particular.
                    shared: !whoId,
                    ...(options ? { options } : {}),
                    ...(opts.productOptions ? { product_options: opts.productOptions } : {}),
                    practice: tour.open } });
            setQtyFor(null);
            onAdded(r, opts);
        } catch (e) {
            toast.error(e.message === 'member_cannot_add' ? t('grocery.cannotAdd') : t('grocery.addFailed'));
        } finally {
            setBusy(false);
            setTimeout(() => setTappedIcon(null), 400);
        }
    };

    /** The treasure card telling the server what happened to it. Fire and
     *  forget: a deal card must never be able to break the list. */
    const treasureEvent = (treasureId, action) => {
        api('/treasure/event', { method: 'POST', session, lang,
            body: { treasure_id: treasureId, action } }).catch(() => {});
    };

    /** Tapping a food asks HOW MANY first, unless the grocer turned that off. */
    const tapFood = async (food, from) => {
        if (who && who.can_add === false) { toast.error(t('grocery.cannotAdd')); return; }
        if (data.quantity?.ask === false) { add(food, Math.max(1, Number(food.last_qty) || 1)); return; }
        setQtyFor({ ...food, from });
        // WHAT KIND, WHICH BRAND — fetched for the card that is already open, so
        // a plain add still costs exactly one tap.
        setPo(null); setPoVal(PO_EMPTY); setPoError(false);
        if (!food.catalog_id) return;
        try {
            const r = await api(`/options/${food.catalog_id}`, { session, lang });
            if (r && r.group) {
                setPo(r);
                setPoVal(r.preselect ? { ...PO_EMPTY, ...r.preselect } : PO_EMPTY);
            }
        } catch (_e) {
            // "NO CHOICES" AND "COULD NOT READ THE CHOICES" ARE NOT THE SAME.
            setPoError(true);
        }
    };

    /** The chips belong to WHOEVER IS ADDING. Switching person on an open card
     *  re-reads them, so nobody inherits someone else's usual brand. */
    useEffect(() => {
        if (!qtyFor?.catalog_id) return;
        let dead = false;
        (async () => {
            try {
                const r = await api(`/options/${qtyFor.catalog_id}`, { session, lang });
                if (dead) return;
                setPo(r && r.group ? r : null);
                setPoVal(r && r.preselect ? { ...PO_EMPTY, ...r.preselect } : PO_EMPTY);
                setPoError(false);
            } catch (_e) { if (!dead) setPoError(true); }
        })();
        return () => { dead = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [whoId]);

    const reloadPo = async () => {
        if (!qtyFor?.catalog_id) return;
        try {
            const r = await api(`/options/${qtyFor.catalog_id}`, { session, lang });
            setPo(r && r.group ? r : null);
            setPoError(false);
        } catch (_e) { setPoError(true); }
    };

    /** The chips, or one line saying they could not be read — never nothing. */
    const optionsSlot = (testId) => {
        if (po) {
            return (
                <ProductOptionsCard api={api} session={session} lang={lang}
                    catalogId={qtyFor.catalog_id} options={po} value={poVal}
                    setValue={setPoVal} reload={reloadPo} testId={testId} />
            );
        }
        if (!poError) return null;
        return (
            <div className="flex flex-wrap items-center gap-2" data-testid={`${testId}-failed`}>
                <p className="text-xs font-semibold text-muted-foreground">
                    {t('grocery.optCouldNotLoad')}
                </p>
                <button onClick={reloadPo} data-testid={`${testId}-retry`}
                    className="min-h-[44px] text-xs font-bold text-emerald-700 underline dark:text-emerald-300">
                    {t('grocery.tryAgain')}
                </button>
            </div>
        );
    };

    /** THE NEW ROW, FOUND. Once the refreshed list has painted, scroll the row
     *  that just arrived into the middle of the screen and let the green fade
     *  after 1.6 s. `smooth` is asked for only when the device has not asked
     *  for less motion. */
    useEffect(() => {
        if (!flashId) return;
        const row = document.querySelector(`[data-testid="grocery-item-${flashId}"]`);
        if (row) {
            const still = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
            row.scrollIntoView({ block: 'center', behavior: still ? 'auto' : 'smooth' });
        }
        clearTimeout(flashTimer.current);
        flashTimer.current = setTimeout(() => setFlashId(null), 1600);
        return () => clearTimeout(flashTimer.current);
    }, [flashId, data?.items]);

    /** Anything that adds a food lands here, from every surface: the aisle
     *  picker, Quick add, a favourite, the treasure card, a typed food and the
     *  chef's "Add as written" / "Add everything". ONE confirmation, the same
     *  everywhere: "Added" with a green check and the item's name, for exactly
     *  two seconds, then it closes itself.
     *
     *  No quantity buttons and no Undo button on it any more — a number is
     *  changed on the list row, and the row's own bin is the Undo. A
     *  confirmation that has to be read AND operated is not a confirmation. */
    const onAdded = (r, opts = {}) => {
        // During the tour the add IS the step: the food landed, so move on.
        if (r?.id && tour.open) setTourGoTo((g) => ({ id: 'tick', n: (g?.n || 0) + 1 }));
        // Skipped during the tour (it would sit over the ? button) and when the
        // surface shows its own confirmation in place — the treasure card turns
        // into the "Added" message itself, and two would be one too many.
        if ((r?.id || r?.name) && !tour.open && !opts.silent) {
            setJustAdded({ id: r.id, name: r.name, qty: Number(r.qty || 1) });
            clearTimeout(addedTimer.current);
            addedTimer.current = setTimeout(() => setJustAdded(null), ADDED_MS);
        }
        // SHOW THEM WHERE IT LANDED. The list can be long and grouped by shelf,
        // so an add with no answer on screen reads as "did that work?". The new
        // row is scrolled into view and held in a soft green for a moment —
        // a colour change only, so reduced-motion settings are respected.
        if (r?.id) setFlashId(r.id);
        load();
    };



    const remove = async (item) => {
        await api(`/items/${item.id}`, { method: 'DELETE', session, lang });
        setLastRemoved(item);
        await load();
        toast(t('grocery.removed', { name: item.name }), {
            action: { label: t('grocery.undo'), onClick: async () => {
                await api(`/items/${item.id}/restore`, { method: 'POST', session, lang });
                setLastRemoved(null); load();
            } },
        });
    };

    const toggleCheck = async (item) => {
        const next = !item.checked;
        if (next) { setTicking(item.id); tickFeedback(); }        // bouncy + pop + haptic
        try {
            const r = await api(`/items/${item.id}`, { method: 'PATCH', session, lang,
                body: { checked: next } });
            if (r.list_completed && !celebrated.current && data?.engagement?.celebrate_enabled !== false) {
                celebrated.current = true;
                setCelebrate(true);
            }
        } finally {
            setTimeout(() => setTicking(null), 450);
            load();
        }
    };

    /** The heart on a list row: keep this food as a favourite, one tap. Uses the
     *  same favourites the add flow writes, so both hearts mean one thing. */
    const toggleFav = async (item) => {
        const on = favIds.has(item.catalog_id || item.saved_id);
        try {
            if (item.saved_id && !item.catalog_id) {
                if (on) {
                    await api(`/favourites/custom/${item.saved_id}`,
                        { method: 'DELETE', session, lang });
                    toast(t('grocery.favouriteRemoved', { name: item.name }));
                } else {
                    await api('/favourites/custom', { method: 'POST', session, lang,
                        body: { saved_id: item.saved_id, options: item.options || [] } });
                    toast.success(t('grocery.favouriteAdded', { name: item.name }));
                }
                load();
                return;
            }
            if (on) {
                await api(`/favourites/${item.catalog_id}`, { method: 'DELETE', session, lang });
                toast(t('grocery.favouriteRemoved', { name: item.name }));
            } else {
                await api('/favourites', { method: 'POST', session, lang,
                    body: { catalog_id: item.catalog_id, options: item.options || [], member_id: whoId } });
                toast.success(t('grocery.favouriteAdded', { name: item.name }));
            }
            load();
        } catch (_e) { toast.error(t('grocery.addFailed')); }
    };

    /** The tour drives the REAL screen: it opens the real aisles and turns the
     *  real tick mode on, so every step highlights something that works. */
    const onTourStep = useCallback((step) => {
        if (step.id === 'aisle') setAddOpen(true);
        else if (step.id === 'tick') { setAddOpen(false); setCheckMode(true); }
        else if (['heart', 'chef', 'points', 'ready', 'done'].includes(step.id)) setAddOpen(false);
    }, []);

    /** SHARE: the same button, now four ways to hand the list to a person.
     *  It opens the panel in the page; nothing is sent from our servers. */
    const share = () => setShareOpen((v) => !v);

    // FROSTED BACKING, ONLY WITH THE GROCER'S PATTERN BEHIND IT. Without the
    // pattern every panel looks exactly as it did before.
    const frost = data?.brand_pattern?.enabled ? ' yot-frost' : '';

    if (!data) {
        return (
            <div className="flex flex-col items-center justify-center gap-3 py-16"
                data-testid="grocery-loading">
                {loadError ? (
                    <>
                        <p className="text-sm font-semibold text-foreground"
                            data-testid="grocery-load-failed">{t('grocery.couldNotLoad')}</p>
                        <button onClick={load} data-testid="grocery-load-retry"
                            className="min-h-[44px] rounded-full bg-emerald-700 px-5 text-sm font-bold text-white">
                            {t('grocery.tryAgain')}
                        </button>
                    </>
                ) : <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />}
            </div>
        );
    }

    const eng = data.engagement || {};
    const favourites = (data.favourites || {})[favShelf] || [];
    const myPicks = kidView
        ? (data.items || []).filter((i) => i.added_by_member_id === sessionMember.id)
        : [];
    const bigTap = kidView ? 'min-h-[56px] text-base' : 'min-h-[44px] text-sm';

    return (
        <div className="relative z-10 space-y-5" data-testid="grocery-list-surface">
            {/* The grocer's own pattern, behind everything, taps pass through. */}
            <BrandPattern pattern={data.brand_pattern} />
            {offline ? (
                <p className="rounded-2xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
                    data-testid="grocery-offline-note">{t('grocery.offlineNote')}</p>
            ) : null}
            <CelebrationBurst show={celebrate} message={t('grocery.allDoneCheer')}
                onDone={() => setCelebrate(false)} testId="grocery-celebration" />

            {/* Household + store */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className={`${compact ? 'text-lg' : 'text-2xl sm:text-3xl'} font-bold text-foreground`}
                        style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="grocery-household-name">
                        {t('grocery.householdList', { name: data.household?.name })}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
                        <Store className="w-4 h-4 shrink-0" />
                        {data.household?.store?.name
                            ? data.household.store.name
                            : t('grocery.outsideServiceArea')}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <GroceryModeToggle memberKey={data?.session_member_id || 'fridge'} />
                    <button onClick={tour.start} data-testid="grocery-help-btn"
                        aria-label={t('groceryTour.help')} title={t('groceryTour.help')}
                        className="flex min-h-[56px] min-w-[56px] items-center justify-center rounded-full border-2 border-emerald-300 bg-emerald-50 text-emerald-700 transition-colors hover:bg-emerald-100 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300">
                        <HelpCircle className="h-7 w-7" />
                    </button>
                    <Button variant={shareOpen ? 'default' : 'outline'} onClick={share}
                        className="min-h-[44px] gap-2" data-testid="grocery-share-btn">
                        <Share2 className="w-4 h-4" />{t('grocery.share')}
                    </Button>
                    <Button variant={checkMode ? 'default' : 'outline'} className="min-h-[44px] gap-2"
                        onClick={() => setCheckMode((v) => !v)} data-testid="grocery-check-mode-btn">
                        <Check className="w-4 h-4" />{t('grocery.checkOff')}
                    </Button>
                    {onMembers && owner && !kidView && (
                        <Button variant="outline" className="min-h-[44px] gap-2" onClick={onMembers}
                            data-testid="grocery-members-btn">
                            <Users className="w-4 h-4" />{t('grocery.membersLabel')}
                        </Button>
                    )}
                </div>
            </div>

            <ChefWelcomeNote config={chef.config} api={chef.api}
                quiet={tour.open || readingOrder} />

            {/* SHARE, IN THE PAGE: text message, email, copy link, copy as text. */}
            {shareOpen ? (
                <GrocerySharePanel api={api} session={session} lang={lang}
                    onClose={() => setShareOpen(false)} />
            ) : null}

            {/* The play row: points, progress, streak, sound — always visible */}
            <div className="flex flex-wrap items-center gap-3" data-testid="grocery-play-row">
                <PointsChip points={eng.points || 0} programName={eng.program_name}
                    sample={eng.sample !== false} testId="grocery-points-chip" />
                <StreakChip streak={eng.streak} testId="grocery-streak-chip" />
                <SoundToggle testId="grocery-sound-toggle" />
                <RewardProgress reward={eng.reward} sample={eng.sample !== false}
                    testId="grocery-reward-progress" />
            </div>

            {/* THE ONE CONFIRMATION. "Added", a green check, the name — two
                seconds, then it closes itself. Nothing to operate: the quantity
                lives on the list row and the row's bin is the Undo. */}
            {justAdded ? (
                <div className="fixed left-1/2 top-[5.5rem] z-[90] flex w-[min(92vw,30rem)] -translate-x-1/2 items-center gap-2 rounded-2xl border border-emerald-300 bg-emerald-50 px-3 py-3 shadow-xl transition-opacity duration-200 dark:border-emerald-500/40 dark:bg-emerald-900"
                    role="status" aria-live="polite"
                    data-testid="grocery-added-toast">
                    <Check className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-300" />
                    <span className="min-w-0 flex-1 truncate text-sm font-semibold text-emerald-900 dark:text-emerald-200">
                        <span className="font-black">{t('grocery.added')}</span>
                        {' · '}
                        {justAdded.name}
                    </span>
                </div>
            ) : null}

            {/* TODAY'S TREASURE — rotated per household per day, never shown
                when it is already on the list, and gone once it is added. */}
            <TreasureCard frost={frost} treasure={eng.treasure}
                onAdd={(food) => add(food, 1, { silent: true })}
                onEvent={treasureEvent}
                onAnother={load} />

            {/* Who's adding? — attribution, not security */}
            <div className={`rounded-2xl border border-slate-200 dark:border-slate-700/60 p-4 bg-white dark:bg-slate-900/40${frost}`}>
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                    {t('grocery.whosAdding')} · {t('grocery.attributionNote')}
                </p>
                <div className="flex flex-wrap gap-2">
                    {members.map((m) => (
                        <button key={m.id} onClick={() => setWhoId(m.id)}
                            data-testid={`grocery-who-${m.id}`}
                            className={`flex items-center gap-2 px-3 rounded-full font-semibold transition-colors ${bigTap} ${
                                whoId === m.id
                                    ? 'bg-emerald-700 text-white'
                                    : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'}`}>
                            <MemberAvatar member={m} size={kidView ? 32 : 26} />
                            {m.name}
                        </button>
                    ))}
                    {/* NOBODY IN PARTICULAR. Deliberately a different shape
                        from the people chips: dashed, a house, no avatar. */}
                    <button onClick={() => setWhoId(null)} data-testid="grocery-who-everyone"
                        className={`flex items-center gap-2 px-4 rounded-full border-2 border-dashed font-semibold ${bigTap} ${
                            whoId === null
                                ? 'border-slate-900 bg-slate-900 text-white dark:border-white dark:bg-white dark:text-slate-900'
                                : 'border-slate-400 text-slate-700 dark:border-slate-500 dark:text-slate-200'}`}>
                        <Home className={kidView ? 'h-5 w-5' : 'h-4 w-4'} />
                        {t('grocery.everyone')}
                    </button>
                </div>
                {/* What the chosen chip actually MEANS, in one line, so nobody
                    has to add something to find out. */}
                <p className="mt-2.5 text-xs text-muted-foreground" data-testid="grocery-who-hint">
                    {who ? t('grocery.whoHintMember', { name: who.name })
                        : t('grocery.whoHintEveryone')}
                </p>
            </div>

            {/* ONE BILL OR SEPARATE BILLS — the grocer bills, the app groups. */}
            <BillingPanel frost={frost} api={api} session={session} lang={lang} members={members}
                billing={data.billing} meId={whoId || data.session_member_id}
                onChanged={load} />

            {/* One way in: the aisles. Full-screen on a phone, a panel on a tablet. */}
            <Button onClick={() => setAddOpen(true)} data-testid="grocery-open-add-flow"
                data-launcher-avoid="lift"
                className="min-h-[60px] w-full gap-2 text-base font-bold">
                <ShoppingBasket className="h-5 w-5" />{t('grocery.addToList')}
            </Button>
            {addOpen ? (
                <GroceryAddFlow frost={frost} session={session} lang={lang} memberId={whoId} practice={tour.open}
                    onAdded={onAdded} onClose={() => { setAddOpen(false); load(); }} />
            ) : null}
            {favSheet ? (
                <FavouriteRoller favourite={favSheet} session={session} lang={lang} memberId={whoId}
                    frost={frost} onAdded={onAdded} onClose={() => setFavSheet(null)} />
            ) : null}

            {/* Favourites, split by where they live: My Fridge / My Pantry */}
            {(data.favourites?.fridge?.length || data.favourites?.pantry?.length) ? (
                <div className={`space-y-2 rounded-2xl${frost ? frost + ' border border-slate-200 p-3 dark:border-slate-700/60' : ''}`}
                    data-testid="grocery-favourites">
                    <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                        {t('grocery.favourites')}
                    </p>
                    <div className="flex gap-2">
                        {[['fridge', FRIDGE_ICON, 'grocery.myFridge'], ['pantry', PANTRY_ICON, 'grocery.myPantry']].map(
                            ([key, icon, label]) => (
                                <button key={key} onClick={() => setFavShelf(key)}
                                    data-testid={`grocery-fav-tab-${key}`}
                                    className={`flex items-center gap-2 rounded-full px-4 font-semibold transition-colors ${bigTap} ${
                                        favShelf === key
                                            ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
                                            : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>
                                    <img src={icon} alt="" aria-hidden="true" width={20} height={20} />
                                    {t(label)}
                                </button>
                            ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {favourites.length === 0 ? (
                            <p className="text-sm text-muted-foreground" data-testid="grocery-fav-empty">
                                {t('grocery.favEmpty')}
                            </p>
                        ) : favourites.map((c) => (
                            <button key={c.catalog_id || c.name} onClick={() => tapFood(c, 'fav')} disabled={busy}
                                data-testid={`grocery-chip-${c.catalog_id || c.name}`}
                                className={`flex items-center gap-2 rounded-full border border-slate-200 px-4 text-foreground transition-colors hover:border-emerald-600 hover:text-emerald-800 dark:hover:text-emerald-200 dark:border-slate-700 ${bigTap}`}>
                                {c.icon ? (
                                    <span className={tappedIcon === (c.catalog_id || c.name) ? 'play-icon-tap' : ''}>
                                        <GroceryIcon icon={c.icon} badge={c.badge} size={24} />
                                    </span>
                                ) : <Plus className="h-4 w-4" />}
                                <span className="text-left">
                                    {c.name}
                                    {(c.options || []).length ? (
                                        <span className="block text-[11px] font-normal text-muted-foreground">
                                            {c.options.join(' · ')}
                                        </span>
                                    ) : null}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            ) : null}

            {/* THE QUANTITY PANEL, right where the food was tapped. */}
            {qtyFor?.from === 'fav' ? (
                <QtyPanel food={qtyFor} busy={busy}
                    max={(qtyFor.sold_by && qtyFor.sold_by !== 'each')
                        ? (data.quantity?.max_packages || 10) : (data.quantity?.max_counted || 99)}
                    packages={!!(qtyFor.sold_by && qtyFor.sold_by !== 'each')}
                    onAdd={(n) => add(qtyFor, n, { productOptions: po ? poVal : undefined })}
                    onCancel={() => setQtyFor(null)}
                    onEditOptions={qtyFor.catalog_id && !po
                        ? () => { const food = qtyFor; setQtyFor(null); setFavSheet(food); } : undefined}
                    options={optionsSlot('grocery-product-options-fav')}
                    testId="grocery-qty-panel-fav" />
            ) : null}

            {/* Quick add — the items most households reach for first */}
            {data.quick_add?.length > 0 && (
                <div className="space-y-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('grocery.quickAdd')}</p>
                    <div className="flex flex-wrap gap-2">
                        {data.quick_add.map((c) => (
                            <button key={c.catalog_id || c.name} onClick={() => tapFood(c, 'quick')} disabled={busy}
                                data-testid={`grocery-quick-${c.catalog_id || c.name}`}
                                className={`rounded-full border border-slate-200 px-4 text-foreground transition-colors hover:border-emerald-600 hover:text-emerald-800 dark:hover:text-emerald-200 dark:border-slate-700 ${bigTap}`}>
                                + {c.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* THE QUANTITY PANEL, right where the food was tapped. */}
            {qtyFor?.from === 'quick' ? (
                <QtyPanel food={qtyFor} busy={busy}
                    max={(qtyFor.sold_by && qtyFor.sold_by !== 'each')
                        ? (data.quantity?.max_packages || 10) : (data.quantity?.max_counted || 99)}
                    packages={!!(qtyFor.sold_by && qtyFor.sold_by !== 'each')}
                    onAdd={(n) => add(qtyFor, n, { productOptions: po ? poVal : undefined })}
                    onCancel={() => setQtyFor(null)}
                    onEditOptions={qtyFor.catalog_id && !po
                        ? () => { const food = qtyFor; setQtyFor(null); setFavSheet(food); } : undefined}
                    options={optionsSlot('grocery-product-options-quick')}
                    testId="grocery-qty-panel-quick" />
            ) : null}

            {/* The kid's own corner — no timers, no pressure, just their picks */}
            {kidView && (
                <div className={`rounded-2xl border border-sky-200 bg-sky-50 p-4 dark:border-sky-500/30 dark:bg-sky-500/10${frost}`}
                    data-testid="grocery-kid-picks">
                    <p className="flex items-center gap-2 text-sm font-bold text-sky-900 dark:text-sky-200">
                        <MemberAvatar member={sessionMember} size={28} />
                        {t('grocery.myPicks', { name: sessionMember.name })}
                    </p>
                    {myPicks.length === 0 ? (
                        <p className="mt-1 text-sm text-sky-800/80 dark:text-sky-200/80">{t('grocery.myPicksEmpty')}</p>
                    ) : (
                        <div className="mt-2 flex flex-wrap gap-2">
                            {myPicks.map((i) => (
                                <span key={i.id} data-testid={`grocery-kid-pick-${i.id}`}
                                    className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-sky-900 dark:bg-slate-900 dark:text-sky-200">
                                    {i.icon ? <GroceryIcon icon={i.icon} badge={i.badge} size={20} /> : null}
                                    {i.name}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* The list, grouped by category, A→Z inside each group */}
            {byCategory.length === 0 ? (
                <EmptyState icon={EMPTY_ICON} title={t('grocery.emptyTitle')} line={t('grocery.emptyList')}
                    testId="grocery-empty" />
            ) : byCategory.map(([cat, items], gi) => (
                <div key={cat} className="space-y-2" data-testid={`grocery-group-${cat}`}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {items[0]?.category_name || t(`grocery.cat.${cat}`, cat)}
                    </p>
                    <div className={`rounded-xl border border-slate-200 dark:border-slate-700/60 divide-y dark:divide-slate-700/60 overflow-hidden bg-white dark:bg-slate-900/40${frost}`}>
                        {items.map((i, ii) => {
                            const member = members.find((m) => m.id === i.added_by_member_id);
                            const first = gi === 0 && ii === 0;
                            return (
                                <div key={i.id} className={`flex items-center gap-3 px-4 py-3 transition-colors duration-300 ${kidView ? 'min-h-[60px]' : 'min-h-[44px]'} ${
                                    flashId === i.id ? 'bg-emerald-50 ring-2 ring-inset ring-emerald-400 dark:bg-emerald-900/40' : ''}`}
                                    data-testid={`grocery-item-${i.id}`}
                                    data-flash={flashId === i.id ? 'on' : undefined}>
                                    {checkMode && (
                                        <button onClick={() => toggleCheck(i)} data-testid={`grocery-check-${i.id}`}
                                            data-tour-anchor={first ? 'tick' : undefined}
                                            role="checkbox" aria-checked={!!i.checked} aria-label={i.name}
                                            className={`flex shrink-0 items-center justify-center rounded-md border ${kidView ? 'h-9 w-9' : 'h-7 w-7'} ${
                                                i.checked ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 dark:border-slate-600'} ${
                                                ticking === i.id ? 'play-tick' : ''}`}>
                                            {i.checked && <Check className={kidView ? 'h-6 w-6 text-white' : 'h-4 w-4 text-white'} />}
                                        </button>
                                    )}
                                    {i.icon ? <GroceryIcon icon={i.icon} badge={i.badge} size={kidView ? 34 : 28} alt="" /> : null}
                                    <div className="min-w-0 flex-1">
                                        <p className={`break-words font-medium leading-snug ${kidView ? 'text-base' : 'text-sm'} ${i.checked ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                                            {i.name}
                                            {/* THE QUANTITY IN PARENTHESES, and one tap on
                                                the number opens the same keypad. */}
                                            {' '}
                                            <button onClick={() => setQtyEdit(i)} disabled={!!i.checked}
                                                data-testid={`grocery-item-qty-${i.id}`}
                                                className="font-black tabular-nums text-foreground underline decoration-dotted decoration-2 underline-offset-2 disabled:no-underline">
                                                ({i.qty || 1})
                                            </button>
                                            {(i.options || []).length ? (
                                                <span className="text-muted-foreground font-normal"> · {i.options.join(' · ')}</span>
                                            ) : null}
                                            {i.unit ? <span className="text-muted-foreground font-normal"> · {t(`grocery.unit.${i.unit}`, i.unit)}</span> : null}
                                            {i.sold_by && i.sold_by !== 'each' ? (
                                                <span className="ml-1.5 inline-block text-[11px] font-semibold text-sky-700 dark:text-sky-300" data-testid={`grocery-by-weight-${i.id}`}>
                                                    {t('grocery.byWeight')}
                                                </span>
                                            ) : null}
                                            {i.practice ? (
                                                <span className="ml-1.5 rounded-full bg-[#F58220] px-2 py-0.5 text-[11px] font-bold text-[#13265C]"
                                                    data-testid={`grocery-practice-${i.id}`}>
                                                    {t('groceryTour.practice')}
                                                </span>
                                            ) : null}
                                        </p>
                                        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                            {member ? <MemberAvatar member={member} size={18} /> : null}
                                            {i.added_by_name
                                                ? t('grocery.addedBy', { name: i.added_by_name })
                                                : t('grocery.addedByFridge')}
                                            {i.note ? ` · ${i.note}` : ''}
                                        </p>
                                        {qtyEdit?.id === i.id ? (
                                            <div className="mt-2" data-testid={`grocery-item-qty-edit-${i.id}`}>
                                                <QtyEditRow value={i.qty || 1}
                                                    max={(i.sold_by && i.sold_by !== 'each')
                                                        ? (data.quantity?.max_packages || 10)
                                                        : (data.quantity?.max_counted || 99)}
                                                    packages={!!(i.sold_by && i.sold_by !== 'each')}
                                                    onAdd={async (n) => {
                                                        setQtyEdit(null);
                                                        await api(`/items/${i.id}`, { method: 'PATCH',
                                                            session, lang, body: { qty: n } });
                                                        load();
                                                    }}
                                                    testId={`grocery-item-qty-row-${i.id}`} />
                                            </div>
                                        ) : null}
                                    </div>
                                    {i.catalog_id ? (
                                        <button onClick={() => toggleFav(i)}
                                            data-testid={`grocery-fav-heart-${i.id}`}
                                            data-tour-anchor={i.id === heartRowId ? 'heart' : undefined}
                                            aria-pressed={favIds.has(i.catalog_id)}
                                            aria-label={favIds.has(i.catalog_id)
                                                ? t('grocery.removeFavourite') : t('grocery.addFavourite')}
                                            className="flex min-h-[44px] w-9 items-center justify-center p-0">
                                            <Heart className={`h-[18px] w-[18px] transition-colors ${favIds.has(i.catalog_id)
                                                ? 'fill-rose-500 text-rose-500' : 'text-slate-300 dark:text-slate-600'}`} />
                                        </button>
                                    ) : null}
                                    <button onClick={() => remove(i)} data-testid={`grocery-remove-${i.id}`}
                                        className="flex min-h-[44px] min-w-[44px] items-center justify-center p-2 text-muted-foreground hover:text-rose-500">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}

            {/* PHASE 2 — the list becomes an order. The server decides everything
                that matters; this button only asks. */}
            {/* The four-word story, live. A member who cannot approve sees
                "Waiting for <name>" here and no PIN, ever. */}
            <GroceryOrderStatus frost={frost} session={session} memberId={sessionMember?.id || whoId}
                canApprove={!!(sessionMember ? sessionMember.can_approve : owner && !kidView)}
                isDemo={!!tour.state?.is_demo} onChanged={load}
                onReading={setReadingOrder} />

            <GrocerySendList session={session} memberId={whoId} tourState={tour.state}
                itemCount={(data?.items || []).filter((i) => !i.practice && !i.checked).length}
                onSent={() => load()} />

            <ChefPersonaCard config={chef.config} onOpen={(p) => setChefOpenWith(p)} />
            <AskChef session={session} lang={lang} config={chef.config} api={chef.api}
                reload={chef.reload} onAdded={(p, opts) => onAdded(p || {}, opts || {})}
                openWith={chefOpenWith}
                onOpened={() => setChefOpenWith(null)} quietBubble={tour.open || readingOrder}
                freezePosition={tour.open}
                hideLauncher={readingOrder} />

            {/* Show me how again — the tour is never a one-time thing */}
            <button onClick={tour.start} data-testid="grocery-tour-replay"
                className="min-h-[56px] text-sm font-semibold text-emerald-700 underline-offset-2 hover:underline dark:text-emerald-400">
                {t('groceryTour.replay')}
            </button>
            <GroceryTour session={session} kid={kidView} state={tour.state} open={tour.open}
                onClose={tour.close} onStep={onTourStep} goTo={tourGoTo}
                onFinished={() => { tour.reload(); setCheckMode(false); load(); }} />

            {lastRemoved && (
                <Button variant="outline" className="min-h-[44px] gap-2" data-testid="grocery-undo-btn"
                    data-launcher-avoid="lift"
                    onClick={async () => {
                        await api(`/items/${lastRemoved.id}/restore`, { method: 'POST', session, lang });
                        setLastRemoved(null); load();
                    }}>
                    <Undo2 className="w-4 h-4" />{t('grocery.undoRemove', { name: lastRemoved.name })}
                </Button>
            )}

            {who && who.can_add === false && (
                <Badge variant="outline" className="text-rose-500 border-rose-300" data-testid="grocery-cannot-add">
                    {t('grocery.cannotAdd')}
                </Badge>
            )}
        </div>
    );
}
